import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarCheck, Loader2, TrendingUp, Droplet, Heart, Bell } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "@/App";

const Predictions = () => {
  const { token } = useAuth();
  const [predictions, setPredictions] = useState([]);
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchPredictions();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPredictions = async () => {
    try {
      const [predRes, notifRes] = await Promise.all([
        axios.get(`${API}/predictions`, { headers, withCredentials: true }),
        axios.get(`${API}/notifications/upcoming`, { headers, withCredentials: true })
      ]);
      
      setPredictions(predRes.data.predictions || []);
      setStats(predRes.data.cycle_stats);
      setNotifications(notifRes.data.notifications || []);
    } catch (error) {
      // data loads with fallback state
    } finally {
      setLoading(false);
    }
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case "high": return "bg-green-500/20 text-green-400";
      case "medium": return "bg-yellow-500/20 text-yellow-400";
      default: return "bg-mystic-500/20 text-mystic-300";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="predictions-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <CalendarCheck className="w-8 h-8 text-cycle-ovulation" />
          Predictions
        </h1>
        <p className="text-mystic-200/70">Your cycle forecast based on historical data</p>
      </motion.div>

      {/* Upcoming Notifications */}
      {notifications.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="glass border-mystic-300/20">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Bell className="w-5 h-5 text-mystic-300" />
                Upcoming
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {notifications.map((notif, i) => (
                  <div key={`pred-${i}`} className="flex items-center justify-between glass-light rounded-xl p-3">
                    <div className="flex items-center gap-3">
                      {notif.type === "period_reminder" && <Droplet className="w-5 h-5 text-cycle-menstrual" />}
                      {notif.type === "fertile_window" && <Heart className="w-5 h-5 text-cycle-ovulation" />}
                      {notif.type === "ovulation" && <Heart className="w-5 h-5 text-moon-gold" />}
                      <div>
                        <p className="text-white font-medium">{notif.title}</p>
                        <p className="text-mystic-200/70 text-sm">{notif.message}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-mystic-300 border-mystic-300/30">
                      {notif.days_until === 0 ? "Today" : `${notif.days_until}d`}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Cycle Statistics */}
      {stats && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-mystic-300" />
                Your Cycle Statistics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="glass-light rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-white">{stats.average_cycle_length}</p>
                  <p className="text-mystic-200/70 text-sm">Avg. Cycle</p>
                </div>
                <div className="glass-light rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-white">{stats.shortest_cycle || "-"}</p>
                  <p className="text-mystic-200/70 text-sm">Shortest</p>
                </div>
                <div className="glass-light rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-white">{stats.longest_cycle || "-"}</p>
                  <p className="text-mystic-200/70 text-sm">Longest</p>
                </div>
                <div className="glass-light rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-white">{stats.data_points}</p>
                  <p className="text-mystic-200/70 text-sm">Cycles Logged</p>
                </div>
              </div>
              <p className="text-mystic-200/70 text-sm text-center mt-4">
                Last period: {stats.last_period_date}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Predictions */}
      {predictions.length > 0 ? (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-lg text-white">Upcoming Cycles</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {predictions.map((pred, i) => (
                  <motion.div
                    key={`pred-card-${i}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 * i }}
                    className="glass-light rounded-xl p-4"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-white font-medium">Cycle {i + 1}</h4>
                      <Badge className={getConfidenceColor(pred.confidence)}>
                        {pred.confidence} confidence
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-mystic-300 text-sm mb-1">Period</p>
                        <p className="text-white flex items-center gap-2">
                          <Droplet className="w-4 h-4 text-cycle-menstrual" />
                          {pred.period_start} - {pred.period_end}
                        </p>
                      </div>
                      <div>
                        <p className="text-mystic-300 text-sm mb-1">Fertile Window</p>
                        <p className="text-white flex items-center gap-2">
                          <Heart className="w-4 h-4 text-cycle-ovulation" />
                          {pred.fertile_window_start} - {pred.fertile_window_end}
                        </p>
                      </div>
                      <div>
                        <p className="text-mystic-300 text-sm mb-1">Ovulation</p>
                        <p className="text-white flex items-center gap-2">
                          <span className="text-moon-gold">✦</span>
                          {pred.ovulation_date}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      ) : (
        <Card className="glass">
          <CardContent className="py-12 text-center">
            <CalendarCheck className="w-12 h-12 text-mystic-400 mx-auto mb-4" />
            <p className="text-mystic-200/70">
              Log your period start dates to get predictions.
            </p>
            <p className="text-mystic-300 text-sm mt-2">
              Go to Calendar and mark your period start days.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Accuracy Note */}
      <Card className="glass-heavy">
        <CardContent className="py-6 text-center">
          <p className="text-mystic-200/70 text-sm">
            <span className="text-mystic-300">Note:</span> Predictions improve with more data. 
            Log at least 3 cycles for medium confidence, 6+ for high confidence predictions.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Predictions;
